import React, { FunctionComponent, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import Header from 'ui/atoms/header';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import * as Styled from './styled';
import Section from 'src/ui/atoms/section';
import Hint from 'src/ui/atoms/hint';
import { useAddInvestment } from './hooks';
import useIsMedia, { DEVICES } from 'ui/hooks/use-is-media';
import { AdminInvestorList } from 'src/api';
import { FileWithDocType } from 'ui/atoms/file-upload';
import { useMultipleSingleFileUploads } from 'ui/atoms/file-upload/hooks/use-multiple-single-file-uploads';
import Spacer from 'ui/atoms/spacer';
import Button from 'src/ui/atoms/button';
import useTranslate from 'src/ui/hooks/use-translate';
import { handleError } from 'src/ui/helper/error-handling';

interface AddInvestmentModalProps {
  campaignId: string;
  onAddNewInvestment: () => void;
  onClose: () => void;
}

export interface AddInvestmentFields {
  investor: string;
  units: string;
  disagio?: string;
  exemptionOrderAmount?: string;
  signedAt: Date;
  paidAt: Date;
  acceptedAt: Date;
  walletId: string;
  documents?: FileWithDocType[];
}

export const Form = makeModalForm<AddInvestmentFields>();

const AddInvestmentModal: FunctionComponent<AddInvestmentModalProps> = ({
  campaignId,
  onAddNewInvestment,
  onClose,
}) => {
  const {
    documentsUploaded,
    onChangeDocument,
    addMoreDocuments,
    error: fileUploadError,
  } = useMultipleSingleFileUploads();

  const [selectedInvestor, setSelectedInvestor] = useState<AdminInvestorList | null>(null);

  const { debouncedSearch, investors, validationError, setError, getDocumentTypes, onSubmit, wallets, serverError } =
    useAddInvestment(campaignId, documentsUploaded, onAddNewInvestment, selectedInvestor?.id);

  const isPhoneTablet = useIsMedia(DEVICES.phoneTablet);

  const { getErrorForField: getWalletIdError } = handleError({
    error: serverError,
    translate: useTranslate(),
    specialCodes: {
      product_wallet_assignment_missing: {
        field: 'wallet_id',
        translateKey: 'addInvestment.errors.productWalletAssignmentMissingError',
      },
    },
  });

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addInvestment"
        onSubmit={(values: StateValues<AddInvestmentFields>) => {
          onSubmit(values);
        }}
        error={serverError}
      >
        <ModalHeader>
          <Translate name="addInvestment.title" />
        </ModalHeader>
        <ModalContent>
          <>
            <Section spacing="regular">
              <Form.Group name="investor" required={true}>
                <Form.Select
                  options={investors.map(({ id, investorName }) => ({
                    value: id,
                    label: investorName,
                  }))}
                  onInputChange={debouncedSearch}
                  onChange={(investorId) => {
                    const investor = investors.find((investor) => investor.id === investorId) || null;
                    setSelectedInvestor(investor);
                  }}
                ></Form.Select>
              </Form.Group>
            </Section>

            <Section spacing="regular">
              <Styled.SameLine>
                <Form.Group name="units" required={true} spacing={isPhoneTablet ? 'medium' : 'none'}>
                  <Form.Input />
                </Form.Group>
                <Spacer x={4} />
                <Form.Group name="disagio" spacing={isPhoneTablet ? 'medium' : 'none'} required={false}>
                  <Form.Input isCurrency />
                </Form.Group>
              </Styled.SameLine>
            </Section>

            <Section spacing="regular">
              <Form.Group name="exemptionOrderAmount">
                <Form.Input isCurrency />
              </Form.Group>
            </Section>

            <Section spacing="regular">
              <Section spacing="regular">
                <Header spacing="medium">
                  <Translate name="addInvestment.fields.investmentDates.label" />
                </Header>
                <Form.Group name="signedAt" required={true} labelSize="small" labelSpacing="tiny">
                  <Form.DateInput max={new Date()} hasTime={true} />
                </Form.Group>
                <Form.Group name="acceptedAt" required={true} labelSize="small" labelSpacing="tiny">
                  <Form.DateInput max={new Date()} hasTime={true} />
                </Form.Group>
                <Form.Group name="paidAt" required={true} labelSize="small" labelSpacing="tiny">
                  <Form.DateInput max={new Date()} hasTime={true} />
                </Form.Group>
              </Section>
              {selectedInvestor && (
                <Form.Group name="walletId" required={true} fieldError={getWalletIdError('wallet_id')}>
                  <Form.Select
                    options={
                      wallets.map((wallet) => ({
                        value: wallet.id,
                        label: wallet.ethInfo?.address,
                      })) || []
                    }
                  ></Form.Select>
                </Form.Group>
              )}
            </Section>
            <Form.Group name="documents">
              {documentsUploaded &&
                documentsUploaded.map((documentUploaded, index) => (
                  <>
                    <Form.FileUpload
                      key={documentUploaded.index}
                      onChange={(document: FileWithDocType) => {
                        setError('');
                        onChangeDocument(document, documentUploaded.index);
                      }}
                      documentTypes={getDocumentTypes()}
                    />
                    {index !== documentsUploaded.length - 1 && <Spacer y={4} />}
                  </>
                ))}
            </Form.Group>
            {(validationError || fileUploadError) && (
              <Hint variant="danger">
                <Translate name={validationError || fileUploadError} />
              </Hint>
            )}
            <Button variant="link" icon="plus" size="small" inline iconSize="small" onClick={addMoreDocuments}>
              <Translate name="addInvestment.addMoreDocuments" />
            </Button>
            <Form.GenericErrorMessages />
          </>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="addInvestment.title" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddInvestmentModal;
